import { useCallback, useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/webpack";
import { handleDynamicFontResize } from "../../../../../uploaderComponents/helpers/processFont";
import { handleMouseMove } from "../../../../../uploaderComponents/helpers/mouseEvents.js/mouseMove";
import { handleMouseUp } from "../../../../../uploaderComponents/helpers/mouseEvents.js/mouseUp";
import { handleKeyDown } from "../../../../../uploaderComponents/helpers/keyEvents.js/keyDown";
import {
  arrayBufferToBase64DataUrl,
  base64ToUint8Array,
  hasPDFHeader,
} from "../utils/pdfDataUtils";
import { useUndoRedo } from "./useUndoRedo";

const normalizeEditorFields = (fields = []) =>
  (fields || []).map((field) => {
    if (field?.type !== "PDFTextField") {
      return field;
    }

    return {
      ...field,
      autoFontSize: true,
    };
  });

export const usePdfEditorState = ({
  formData,
  isOpen,
  onClose,
  onSave,
  onNotify,
}) => {
  const [currentProcessingFile, setCurrentProcessingFile] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [pdfSize, setPdfSize] = useState(800);
  const [actualWidth, setActualWidth] = useState(612);
  const [actualHeight, setActualHeight] = useState(792);
  const [pdfInput, setPdfInput] = useState(null);
  const [base64File, setBase64File] = useState(null);

  const [resizing, setResizing] = useState(false);
  const [moving, setMoving] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [rect, setRect] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [selectionRect, setSelectionRect] = useState(null);
  const [freeSelectStartPoint, setFreeSelectStartPoint] = useState(null);
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [initialMousePosition, setInitialMousePosition] = useState({
    x: 0,
    y: 0,
  });
  const [initialInputSize, setInitialInputSize] = useState({
    width: 0,
    height: 0,
  });
  const [inputPosition, setInputPosition] = useState({ x: 0, y: 0 });
  const [currCursor, setCurrCursor] = useState("default");
  const [resizeDirection, setResizeDirection] = useState("");

  const [selectToolActive, setSelectToolActive] = useState(true);
  const [typingToolActive, setTypingToolActive] = useState(false);
  const [expandToolBox, setExpandToolBox] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [reverseDeleted, setReverseDeleted] = useState([]);
  const [focusedField, setFocusedField] = useState(null);
  const [highlightFields, setHighlightFields] = useState(true);
  const [textSelectionMode, setTextSelectionMode] = useState(true);
  const [textOverlayDisplayMode, setTextOverlayDisplayMode] = useState("label");
  const [unassignedFieldNames, setUnassignedFieldNames] = useState([]);
  const [activeEditorTool, setActiveEditorTool] = useState("edit");

  const pdfContainerRef = useRef(null);
  const pdfScrollContainerRef = useRef(null);
  const pdfDocumentRef = useRef(null);
  const extractedOnceRef = useRef(false);
  const initializedFormKeyRef = useRef(null);
  const inputRef = useRef(null);
  const childInputRef = useRef(null);

  const { pushHistory, undo, redo, canRedo, canUndo } = useUndoRedo(
    currentProcessingFile,
    setCurrentProcessingFile,
  );

  const fixedInputs = [
    "notary_seal_a",
    "notary_sign_a",
    "notary_seal_b",
    "notary_sign_b",
    "sign_a",
    "sign_b",
    "sign_c",
  ];

  const handleFieldChange = useCallback((e, field) => {
    const newValue = e.target.value;
    setCurrentProcessingFile((prevFile) => ({
      ...prevFile,
      document: {
        ...prevFile.document,
        tempFields: {
          ...prevFile.document.tempFields,
          formFields: prevFile.document.tempFields.formFields.map((f) => {
            if (f.name !== field.name) return f;
            if (!f.autoFontSize) return { ...f, value: newValue };

            const { newFontSize, fontSizes } = handleDynamicFontResize(
              field.width,
              field.height,
              f.fontSize,
              newValue,
              f.fontSizes || [],
              f.value,
            );

            return { ...f, value: newValue, fontSize: newFontSize, fontSizes };
          }),
        },
      },
    }));
  }, []);

  const validateAndProcessFile = useCallback(async (file) => {
    if (!file || file.type !== "application/pdf") return;

    const reader = new FileReader();
    reader.onload = async function () {
      const base64String = reader.result;
      const uint8Data = base64ToUint8Array(base64String);
      if (!hasPDFHeader(uint8Data)) return;

      try {
        await pdfjsLib.getDocument({ data: uint8Data }).promise;
        setBase64File(base64String);
        setCurrentProcessingFile((prevFile) => ({
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...prevFile.document.tempFields,
              currentDocument: {
                ...prevFile.document.tempFields.currentDocument,
                base64File: base64String,
                files: "",
              },
            },
          },
        }));
      } catch (e) {
        console.error("Error loading PDF:", e);
      }
    };

    reader.readAsDataURL(file);
  }, []);

  useEffect(() => {
    if (!formData || !isOpen) return;

    const formIdentityKey =
      formData?.form_id ||
      formData?._id ||
      formData?.id ||
      formData?.fileName ||
      "default_form";

    if (initializedFormKeyRef.current === formIdentityKey) {
      return;
    }

    initializedFormKeyRef.current = formIdentityKey;

    if (formData?.skipPdfInit) {
      setBase64File(null);
      setPdfInput(null);
      return;
    }

    setCurrentProcessingFile({
      document: {
        tempFields: {
          currentDocument: {
            base64File: formData.base64File || null,
            pdfSize,
            actualWidth,
            actualHeight,
            pageXMin: 0,
            pageYMin: 0,
            viewport: null,
            scaleFactor: pdfSize / (actualWidth || 1),
          },
          formFields: normalizeEditorFields(formData.formFields || []),
          pdfTextItemsByPage: formData.pdfTextItemsByPage || {},
          pdfTextEdits: formData.pdfTextEdits || [],
          selectedPdfTextItem: null,
          selectedExtraPdfTextItems: [],
          selectedFormField: null,
          selectedExtraFields: [],
          formFieldTools: {
            textField: { active: false, name: "textField" },
            checkbox: { active: false, name: "checkbox" },
            insertText: { active: false, name: "insertText" },
            rectangle: { active: false, name: "rectangle" },
            line: { active: false, name: "line" },
            radio: { active: false, name: "radio" },
            date: { active: false, name: "date" },
            notarySeal_a: {
              active: false,
              name: "notary_seal_a",
              disabled: false,
            },
            notarySignature_a: {
              active: false,
              name: "notary_sign_a",
              disabled: false,
            },
            notarySeal_b: {
              active: false,
              name: "notary_seal_b",
              disabled: false,
            },
            notarySignature_b: {
              active: false,
              name: "notary_sign_b",
              disabled: false,
            },
            signatureA: { active: false, name: "sign_a", disabled: false },
            signatureB: { active: false, name: "sign_b", disabled: false },
            signatureC: { active: false, name: "sign_c", disabled: false },
          },
        },
      },
    });

    (async () => {
      if (formData.pdfFile) {
        await validateAndProcessFile(formData.pdfFile);
      } else if (formData.base64File) {
        setBase64File(formData.base64File);
        setPdfInput(formData.base64File);
      } else if (formData.pdfUrl) {
        const res = await fetch(formData.pdfUrl, { credentials: "include" });
        if (!res.ok) throw new Error(`Failed to fetch PDF: ${res.status}`);
        const b64 = arrayBufferToBase64DataUrl(await res.arrayBuffer());
        setBase64File(b64);
        setPdfInput(b64);
        setCurrentProcessingFile((prev) => ({
          ...prev,
          document: {
            ...prev.document,
            tempFields: {
              ...prev.document.tempFields,
              selectedPdfTextItem: null,
              currentDocument: {
                ...prev.document.tempFields.currentDocument,
                base64File: b64,
              },
            },
          },
        }));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, isOpen, validateAndProcessFile]);

  useEffect(() => {
    if (!currentProcessingFile) return;
    const currentFields =
      currentProcessingFile?.document?.tempFields?.formFields || [];
    if (!unassignedFieldNames.length) return;

    const stillUnassigned = currentFields
      .filter((f) => !f?.block_id || !f?.field_id)
      .map((f) => f?.name)
      .filter(Boolean);

    if (stillUnassigned.length !== unassignedFieldNames.length) {
      setUnassignedFieldNames(stillUnassigned);
    }
  }, [currentProcessingFile, unassignedFieldNames]);

  useEffect(() => {
    const handleWheel = (e) => {
      if (
        !currentProcessingFile?.document?.tempFields?.currentDocument
          ?.base64File
      )
        return;
      if (!e.ctrlKey) return;
      e.preventDefault();
      const currentScale =
        currentProcessingFile?.document?.tempFields?.currentDocument
          ?.scaleFactor || scale;
      const newScale =
        e.deltaY < 0
          ? Math.min(currentScale + 0.1, 2.0)
          : Math.max(currentScale - 0.1, 0.5);
      setScale(newScale);
      setPdfSize(newScale * actualWidth);
      setCurrentProcessingFile((prevFile) => ({
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...prevFile.document.tempFields,
            currentDocument: {
              ...prevFile.document.tempFields.currentDocument,
              pdfSize: newScale * actualWidth,
              scaleFactor: newScale,
            },
          },
        },
      }));
    };

    document.addEventListener("wheel", handleWheel, { passive: false });
    return () => document.removeEventListener("wheel", handleWheel);
  }, [currentProcessingFile, actualWidth, scale]);

  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    currentProcessingFile,
    typingToolActive,
    selectToolActive,
    focusedField,
    currCursor,
    expandToolBox,
  ]);

  useEffect(() => {
    const handleKeyDownWrapper = (e) => {
      handleKeyDown(
        e,
        currentProcessingFile,
        setCurrentProcessingFile,
        typingToolActive,
        setReverseDeleted,
        setCurrCursor,
        selectToolActive,
        focusedField,
        setTypingToolActive,
        setSelectToolActive,
        currCursor,
        setFocusedField,
        setHighlightFields,
        expandToolBox,
        setExpandToolBox,
        activeEditorTool,
      );
    };

    document.addEventListener("keydown", handleKeyDownWrapper);

    return () => {
      document.removeEventListener("keydown", handleKeyDownWrapper);
    };
  }, [
    currentProcessingFile,
    typingToolActive,
    selectToolActive,
    focusedField,
    currCursor,
    expandToolBox,
    activeEditorTool,
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (resizing) return;
      const isInsidePdfViewer = pdfScrollContainerRef.current?.contains(
        event.target,
      );
      const clickedInsideInput = Boolean(event.target?.closest?.("input"));
      if (isInsidePdfViewer && !clickedInsideInput) {
        setCurrentProcessingFile((prevFile) => ({
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...prevFile.document.tempFields,
              selectedFormField: null,
              selectedExtraFields: [],
              selectedPdfTextItem: null,
              selectedExtraPdfTextItems: [],
            },
          },
        }));
      }
    };

    document.addEventListener("dblclick", handleClickOutside);
    return () => document.removeEventListener("dblclick", handleClickOutside);
  }, [resizing]);

  useEffect(() => {
    setCurrentProcessingFile((prev) => {
      if (!prev) return prev;
      const aw =
        prev?.document?.tempFields?.currentDocument?.actualWidth ||
        actualWidth ||
        1;
      const newScale =
        (prev?.document?.tempFields?.currentDocument?.pdfSize || pdfSize) / aw;
      return {
        ...prev,
        document: {
          ...prev.document,
          tempFields: {
            ...prev.document.tempFields,
            currentDocument: {
              ...prev.document.tempFields.currentDocument,
              scaleFactor: newScale,
            },
          },
        },
      };
    });
  }, [pdfSize, actualWidth]);

  useEffect(() => {
    if (!isOpen) {
      extractedOnceRef.current = false;
      initializedFormKeyRef.current = null;
    }
  }, [isOpen]);

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }) => setNumPages(numPages),
    [],
  );

  const onPageLoadSuccess = useCallback(
    (page) => {
      const view = Array.isArray(page?.view) ? page.view : [0, 0, 612, 792];
      const [xMin = 0, yMin = 0, xMax = 612, yMax = 792] = view;
      const width = page.originalWidth ? xMax - xMin : 612;
      const height = page.originalHeight ? yMax - yMin : 792;
      const viewport = page?.getViewport
        ? page.getViewport({ scale, rotation: page.rotate || 0 })
        : null;
      setActualWidth(width);
      setActualHeight(height);
      setPdfSize(scale * width);
      setCurrentProcessingFile((prev) => ({
        ...prev,
        document: {
          ...prev.document,
          tempFields: {
            ...prev.document.tempFields,
            currentDocument: {
              ...prev.document.tempFields.currentDocument,
              actualWidth: width,
              actualHeight: height,
              pageXMin: xMin,
              pageYMin: yMin,
              viewport: viewport
                ? {
                    width: viewport.width,
                    height: viewport.height,
                    rotation: viewport.rotation,
                    transform: Array.isArray(viewport.transform)
                      ? [...viewport.transform]
                      : null,
                  }
                : null,
              pdfSize: scale * width,
              scaleFactor: scale,
              pageNumber,
            },
          },
        },
      }));
    },
    [scale, pageNumber],
  );

  const handleScaleChange = useCallback(
    (_, newValue) => {
      setScale(newValue);
      setPdfSize(newValue * actualWidth);
      setCurrentProcessingFile((prev) => ({
        ...prev,
        document: {
          ...prev.document,
          tempFields: {
            ...prev.document.tempFields,
            currentDocument: {
              ...prev.document.tempFields.currentDocument,
              pdfSize: newValue * actualWidth,
              scaleFactor: newValue,
            },
          },
        },
      }));
    },
    [actualWidth],
  );

  const handlePageChange = useCallback((_, value) => {
    setPageNumber(value);
    setIsSelecting(false);
    setSelectionRect(null);
    setFreeSelectStartPoint(null);
    setCurrentProcessingFile((prev) =>
      prev
        ? {
            ...prev,
            document: {
              ...prev.document,
              tempFields: {
                ...prev.document.tempFields,
                selectedFormField: null,
                selectedExtraFields: [],
                selectedPdfTextItem: null,
                selectedExtraPdfTextItems: [],
                currentDocument: {
                  ...prev.document.tempFields.currentDocument,
                  pageNumber: value,
                },
              },
            },
          }
        : prev,
    );
  }, []);

  useEffect(() => {
    if (!pdfScrollContainerRef.current) return;
    pdfScrollContainerRef.current.scrollTop = 0;
  }, [pageNumber]);

  const handleToolToggle = useCallback((_, newTool) => {
    if (newTool === null) return;
    if (newTool === "hand") {
      setCurrCursor("default");
      setSelectToolActive(true);
      setTypingToolActive(false);
      setCurrentProcessingFile((prevFile) => ({
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...prevFile.document.tempFields,
            selectedFormField: null,
            selectedExtraFields: [],
            formFieldTools: Object.keys(
              prevFile.document.tempFields.formFieldTools,
            ).reduce(
              (acc, key) => ({
                ...acc,
                [key]: {
                  ...prevFile.document.tempFields.formFieldTools[key],
                  active: false,
                  values: [],
                },
              }),
              {},
            ),
          },
        },
      }));
    } else {
      setCurrCursor("default");
      setSelectToolActive(false);
      setTypingToolActive(true);
      setCurrentProcessingFile((prevFile) => ({
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...prevFile.document.tempFields,
            selectedFormField: null,
            selectedExtraFields: [],
          },
        },
      }));
    }
  }, []);

  const handleEditorToolSelection = useCallback(
    (toolKey) => {
      setActiveEditorTool((prevActiveTool) => {
        const nextTool = prevActiveTool === toolKey ? null : toolKey;
        if (
          toolKey === "rectangle" &&
          prevActiveTool === "edit" &&
          currentProcessingFile.document.tempFields?.selectedFormField?.type ===
            "PDFCheckBox"
        ) {
          pushHistory?.();
          const selectedField =
            currentProcessingFile.document.tempFields?.selectedFormField;

          const updatedFormFields = (
            currentProcessingFile.document.tempFields?.formFields || []
          ).map((field) => {
            const isTargetField = selectedField?.field_instance_id
              ? field?.field_instance_id === selectedField?.field_instance_id
              : field?.name === selectedField?.name;

            if (!isTargetField) return field;

            return {
              ...field,
              outline: true,
            };
          });

          const editedSelectedFormField = {
            ...selectedField,
            outline: true,
          };

          setCurrentProcessingFile((prevFile) => ({
            ...prevFile,
            document: {
              ...prevFile.document,
              tempFields: {
                ...prevFile.document.tempFields,
                formFields: updatedFormFields,
                selectedFormField: editedSelectedFormField,
              },
            },
          }));
          return prevActiveTool;
        }

        if (toolKey === "edit") {
          setCurrCursor("default");
          setSelectToolActive(Boolean(nextTool));
          setTypingToolActive(false);
          setCurrentProcessingFile((prevFile) => {
            if (!prevFile) return prevFile;
            return {
              ...prevFile,
              document: {
                ...prevFile.document,
                tempFields: {
                  ...prevFile.document.tempFields,
                  selectedFormField: null,
                  selectedExtraFields: [],
                  selectedPdfTextItem: null,
                  formFieldTools: Object.keys(
                    prevFile.document.tempFields.formFieldTools,
                  ).reduce(
                    (acc, key) => ({
                      ...acc,
                      [key]: {
                        ...prevFile.document.tempFields.formFieldTools[key],
                        active: false,
                      },
                    }),
                    {},
                  ),
                },
              },
            };
          });
        } else if (toolKey === "hand") {
          setCurrCursor(nextTool ? "grab" : "default");
          setSelectToolActive(false);
          setTypingToolActive(false);
          setCurrentProcessingFile((prevFile) => {
            if (!prevFile) return prevFile;
            return {
              ...prevFile,
              document: {
                ...prevFile.document,
                tempFields: {
                  ...prevFile.document.tempFields,
                  selectedFormField: null,
                  selectedExtraFields: [],
                  selectedPdfTextItem: null,
                  selectedExtraPdfTextItems: [],
                  formFieldTools: Object.keys(
                    prevFile.document.tempFields.formFieldTools,
                  ).reduce(
                    (acc, key) => ({
                      ...acc,
                      [key]: {
                        ...prevFile.document.tempFields.formFieldTools[key],
                        active: false,
                      },
                    }),
                    {},
                  ),
                },
              },
            };
          });
        } else {
          const toolMap = {
            checkbox: "checkbox",
            textField: "textField",
            insertText: "insertText",
            rectangle: "rectangle",
            line: "line",
          };
          const mappedTool = toolMap[toolKey];

          if (nextTool) {
            if (toolKey === "insertText") {
              setCurrCursor("text");
            } else {
              setCurrCursor("crosshair");
            }
          } else {
            setCurrCursor("default");
          }

          setSelectToolActive(false);
          setTypingToolActive(false);
          setCurrentProcessingFile((prevFile) => {
            if (!prevFile) return prevFile;
            return {
              ...prevFile,
              document: {
                ...prevFile.document,
                tempFields: {
                  ...prevFile.document.tempFields,
                  selectedFormField: null,
                  selectedExtraFields: [],
                  selectedPdfTextItem: null,
                  selectedExtraPdfTextItems: [],
                  formFieldTools: Object.keys(
                    prevFile.document.tempFields.formFieldTools,
                  ).reduce(
                    (acc, key) => ({
                      ...acc,
                      [key]: {
                        ...prevFile.document.tempFields.formFieldTools[key],
                        active: Boolean(
                          mappedTool && nextTool && key === mappedTool,
                        ),
                      },
                    }),
                    {},
                  ),
                },
              },
            };
          });
        }

        return nextTool;
      });
    },
    [currentProcessingFile, pushHistory],
  );

  const handleSave = useCallback(() => {
    if (!onSave || !currentProcessingFile) return;

    const fields =
      currentProcessingFile?.document?.tempFields?.formFields || [];
    const unassigned = fields.filter((f) => !f?.block_id || !f?.field_id);
    const unassignedNames = unassigned
      .map((f, idx) => f?.name || `field_${idx + 1}`)
      .filter(Boolean);
    setUnassignedFieldNames(unassignedNames);
    onSave({
      ...formData,
      formFields: fields,
      pdfTextEdits:
        currentProcessingFile?.document?.tempFields?.pdfTextEdits || [],
    });
  }, [onSave, currentProcessingFile, formData, onNotify]);

  const handleClose = useCallback(() => {
    setPageNumber(1);
    onClose?.();
  }, [onClose]);

  return {
    currentProcessingFile,
    setCurrentProcessingFile,
    numPages,
    pageNumber,
    scale,
    pdfInput,
    base64File,
    resizing,
    setResizing,
    moving,
    setMoving,
    isDrawing,
    setIsDrawing,
    isSelecting,
    setIsSelecting,
    rect,
    setRect,
    selectionRect,
    setSelectionRect,
    freeSelectStartPoint,
    setFreeSelectStartPoint,
    startPoint,
    setStartPoint,
    initialMousePosition,
    setInitialMousePosition,
    initialInputSize,
    setInitialInputSize,
    inputPosition,
    setInputPosition,
    currCursor,
    setCurrCursor,
    resizeDirection,
    setResizeDirection,
    selectToolActive,
    setSelectToolActive,
    typingToolActive,
    setTypingToolActive,
    expandToolBox,
    setExpandToolBox,
    focusedField,
    setFocusedField,
    highlightFields,
    setHighlightFields,
    textOverlayDisplayMode,
    setTextOverlayDisplayMode,
    unassignedFieldNames,
    activeEditorTool,
    pdfContainerRef,
    pdfScrollContainerRef,
    pdfDocumentRef,
    inputRef,
    childInputRef,
    fixedInputs,
    handleFieldChange,
    onDocumentLoadSuccess,
    onPageLoadSuccess,
    handleScaleChange,
    handlePageChange,
    handleToolToggle,
    handleEditorToolSelection,
    handleSave,
    handleClose,
    pushHistory,
    undo,
    redo,
    canRedo,
    canUndo,
    textSelectionMode,
    setTextSelectionMode,
  };
};
